import type { AxiosError } from 'axios';

/**
 * Read a Blob as text.
 *
 * `Blob.prototype.text()` is the modern path, but it is absent in some
 * environments (jsdom, older Safari), so fall back to FileReader rather than
 * letting a missing method masquerade as an unparseable body.
 */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsText(blob);
  });
}

/**
 * Turn a Blob error body back into the JSON the server actually sent.
 *
 * Any request made with `responseType: 'blob'` (every export and download in
 * the product) gets its *error* body as a Blob too, so the response
 * interceptor's `error.response.data.error` check and the components'
 * `err.message` fallback both miss the real reason. The user is left with
 * axios's generic "Request failed with status code 403", which cannot
 * distinguish a viewer-role export block from an expired session.
 *
 * Mutates `error` in place and resolves when it is safe to read normally.
 */
export async function normalizeBlobErrorResponse(error: AxiosError): Promise<void> {
  const data = error.response?.data;
  if (!(data instanceof Blob)) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(await readBlobText(data));
  } catch {
    // A genuine binary body, or a truncated one. Leave it exactly as it was.
    return;
  }
  if (!parsed || typeof parsed !== 'object') return;

  error.response!.data = parsed;

  const message = (parsed as { error?: unknown; message?: unknown }).error
    ?? (parsed as { message?: unknown }).message;
  if (typeof message === 'string' && message) {
    error.message = message;
  }
}
